import { StatusVariant } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import {
  AllPaymentRequestsInsightsDocument,
  AllPaymentsInsightsDocument,
  PaymentRequestInsightsDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

interface TreasuryInsightCardProps {
  title: string
  amount: number
  currency: string
  locale: string
  count?: number
  statusVariant: StatusVariant
}

const TreasuryInsightCard = ({
  title,
  amount,
  currency,
  locale,
  count,
  statusVariant,
}: TreasuryInsightCardProps) => {
  const formattedAmount = MoneyWithCurrency({ amount, currency, locale })

  return (
    <Widget
      header={{ title }}
      status={count !== undefined ? { text: `${count} items`, variant: statusVariant } : undefined}
      summaries={[{ label: i18n.t('banking.treasury.insights.amount'), value: formattedAmount }]}
    >
      <></>
    </Widget>
  )
}

interface PaymentRequestInsightCardsProps {
  paymentRequestId: string
  currency: string
  locale: string
}

export const PaymentRequestInsightCards = ({
  paymentRequestId,
  currency,
  locale,
}: PaymentRequestInsightCardsProps) => {
  const { data } = useQuery(PaymentRequestInsightsDocument, {
    variables: { paymentRequestId },
    skip: !paymentRequestId,
  })

  const totalAmount = data?.banking?.total?.aggregate?.sum?.amountCents ?? 0
  const totalCount = data?.banking?.total?.aggregate?.count ?? 0
  const paidAmount = data?.banking?.paid?.aggregate?.sum?.amountCents ?? 0
  const paidCount = data?.banking?.paid?.aggregate?.count ?? 0
  const inProgressAmount = data?.banking?.inProgress?.aggregate?.sum?.amountCents ?? 0
  const inProgressCount = data?.banking?.inProgress?.aggregate?.count ?? 0
  const pendingRetryAmount = data?.banking?.rejected?.aggregate?.sum?.amountCents ?? 0
  const pendingRetryCount = data?.banking?.rejected?.aggregate?.count ?? 0

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.total')}
        amount={totalAmount}
        currency={currency}
        locale={locale}
        count={totalCount}
        statusVariant="neutral"
      />
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.paid')}
        amount={paidAmount}
        currency={currency}
        locale={locale}
        count={paidCount}
        statusVariant="positive"
      />
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.inProgress')}
        amount={inProgressAmount}
        currency={currency}
        locale={locale}
        count={inProgressCount}
        statusVariant="info"
      />
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.pendingRetry')}
        amount={pendingRetryAmount}
        currency={currency}
        locale={locale}
        count={pendingRetryCount}
        statusVariant="critical"
      />
    </div>
  )
}

interface AllPaymentRequestsInsightCardsProps {
  currency: string
  locale: string
  dueDateFilter?: { between: [string, string] }
}

export const AllPaymentRequestsInsightCards = ({
  currency,
  locale,
  dueDateFilter,
}: AllPaymentRequestsInsightCardsProps) => {
  const { data } = useQuery(AllPaymentRequestsInsightsDocument, {
    variables: { dueDateFilter },
  })

  const totalAmount = data?.banking?.total?.aggregate?.sum?.amountCents ?? 0
  const totalCount = data?.banking?.total?.aggregate?.count ?? 0
  const paidAmount = data?.banking?.paid?.aggregate?.sum?.amountCents ?? 0
  const paidCount = data?.banking?.paid?.aggregate?.count ?? 0
  const inProgressAmount = data?.banking?.inProgress?.aggregate?.sum?.amountCents ?? 0
  const inProgressCount = data?.banking?.inProgress?.aggregate?.count ?? 0
  const pendingAmount = data?.banking?.pending?.aggregate?.sum?.amountCents ?? 0
  const pendingCount = data?.banking?.pending?.aggregate?.count ?? 0

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.total')}
        amount={totalAmount}
        currency={currency}
        locale={locale}
        count={totalCount}
        statusVariant="neutral"
      />
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.pending')}
        amount={pendingAmount}
        currency={currency}
        locale={locale}
        count={pendingCount}
        statusVariant="warning"
      />
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.inProgress')}
        amount={inProgressAmount}
        currency={currency}
        locale={locale}
        count={inProgressCount}
        statusVariant="info"
      />
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.paid')}
        amount={paidAmount}
        currency={currency}
        locale={locale}
        count={paidCount}
        statusVariant="positive"
      />
    </div>
  )
}

interface AllPaymentsInsightCardsProps {
  currency: string
  locale: string
  dueDateFilter?: { between: [string, string] }
}

export const AllPaymentsInsightCards = ({
  currency,
  locale,
  dueDateFilter,
}: AllPaymentsInsightCardsProps) => {
  const { data } = useQuery(AllPaymentsInsightsDocument, {
    variables: { dueDateFilter },
  })

  const totalAmount = data?.banking?.total?.aggregate?.sum?.amountCents ?? 0
  const totalCount = data?.banking?.total?.aggregate?.count ?? 0
  const completedAmount = data?.banking?.completed?.aggregate?.sum?.amountCents ?? 0
  const completedCount = data?.banking?.completed?.aggregate?.count ?? 0
  const inProgressAmount = data?.banking?.inProgress?.aggregate?.sum?.amountCents ?? 0
  const inProgressCount = data?.banking?.inProgress?.aggregate?.count ?? 0
  const pendingAmount = data?.banking?.pending?.aggregate?.sum?.amountCents ?? 0
  const pendingCount = data?.banking?.pending?.aggregate?.count ?? 0

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.total')}
        amount={totalAmount}
        currency={currency}
        locale={locale}
        count={totalCount}
        statusVariant="neutral"
      />
      <TreasuryInsightCard
        title={i18n.t('finance.overview.payments.requires_attention')}
        amount={pendingAmount}
        currency={currency}
        locale={locale}
        count={pendingCount}
        statusVariant="warning"
      />
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.inProgress')}
        amount={inProgressAmount}
        currency={currency}
        locale={locale}
        count={inProgressCount}
        statusVariant="info"
      />
      <TreasuryInsightCard
        title={i18n.t('banking.treasury.insights.paid')}
        amount={completedAmount}
        currency={currency}
        locale={locale}
        count={completedCount}
        statusVariant="positive"
      />
    </div>
  )
}
